import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import SaveUpdatesToFolder from 'components/Dashboard/SubNav/ui/SaveUpdatesToFolder';
import { store } from 'components/Store';
import { dataHubUsers } from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import { useContext, useEffect, useRef, useState } from 'react';
import RateMultipleUpdatesButton from 'components/Dashboard/SubNav/RateMultipleUpdatesButton';
import RemoveInteractionsModal from 'components/Dashboard/Influence/CrmInteractions/RemoveInteractionsModal';
import AddTagsToInteractionsModal from 'components/Dashboard/Influence/CrmInteractions/AddTagsToInteractionsModal';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';

const MainListActionsBar = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { selectedUpdates, activePlan } = state;

  const { enableReports } = activePlan ?? {};
  const dataHubUser = dataHubUsers(activePlan);
  const { setIsOpenReportPopup, setIsOpenLoggedOut, hasReportableUpdates } = props ?? {};
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenAddTagsModal, setIsOpenAddTagsModal] = useState(false);
  const [isOpenMoreOptions, setIsOpenMoreOptions] = useState(false);
  const moreOptionsRef = useRef(null);
  useOutsideElement(moreOptionsRef, () => setIsOpenMoreOptions(false));

  const location = useLocation();
  const navigate = useNavigate();

  const allInteractions =
    selectedUpdates?.length > 0 && selectedUpdates.every((item) => item?.type === 'CrmInteraction');

  const timeoutRef = useRef(null);

  const { inboxSection, dashboardSection } = useMainSectionParameter();
  const { resetSelectedUpdatesState } = useMainActionsBarFunctions();
  const { isAuthenticated } = useAuth0();

  useEffect(() => {
    if (selectedUpdates?.length > 0) {
      setShouldRender(true);
      timeoutRef.current = setTimeout(() => setIsVisible(true), 10);
    } else if (shouldRender) {
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => setShouldRender(false), 250);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [selectedUpdates, shouldRender]);

  const embargoedUpdates = selectedUpdates?.some((item) => item?.embargoed);

  const addToReportButton = hasReportableUpdates && (
    <button
      className={`contacts-list-bar-action-container-btn d-none d-lg-inline-flex ${
        enableReports && !embargoedUpdates && !dataHubUser ? '' : 'light-50-text'
      }`}
      onClick={() => {
        if (!embargoedUpdates && !dataHubUser) {
          if (enableReports && isAuthenticated) {
            setIsOpenReportPopup(true);
          } else {
            setIsOpenLoggedOut(true);
          }
        }
      }}
      data-tooltip-content={
        embargoedUpdates
          ? 'One or more updates selected are currently under embargo. You’ll be able to add them to reports once the embargo has been lifted.'
          : ''
      }
      data-tooltip-id='general-tooltip'
      data-tooltip-class-name='feed-tooltip'
      data-tooltip-place='bottom'
    >
      <span className='icon-document' />
      Add to a report{' '}
      {(!enableReports || dataHubUser) && <span className='icon-padlock ml-2 paragraph-p2 d-inline-block-centered' />}
    </button>
  );

  return (
    <>
      {shouldRender && (
        <div
          className={`main-list-action-bar bg-light-blue position-absolute top-0 adjustment-of-position w-100 ${
            isVisible ? 'visible' : ''
          }`}
        >
          <div className='mx-auto main-content-wrapper flex-centered justify-content-between'>
            {/* Empty spacer matching the real "People" sidebar column's width (Influence/index.jsx's
            own .side-left-element) - exact same structure as InfluenceCrmSubNav.jsx uses for its
            own contacts-selected bar, so .main-content below starts where the page's content
            column starts instead of drifting under the sidebar. */}
            <div className='side-left-element pl-md-5 pl-3 d-none d-lg-block' />
            <div className='main-content flex-centered filters-general-container justify-content-between pr-xl-5 px-md-4 px-3'>
              <div className='crm-contacts-select-subnav'>
                <div className='crm-contacts-list-bar'>
                  <div className='contacts-list-bar-helper-text-wrapper'>
                    <p
                      className='contacts-list-bar-helper-text text-white nowrap-item'
                      style={{ alignItems: 'center' }}
                    >
                      <SelectedCounter value={selectedUpdates?.length} /> selected
                    </p>
                  </div>
                  <div className='contacts-list-bar-action-container'>
                    {dataHubUser ? (
                      <>
                        <SaveUpdatesToFolder />
                        {(inboxSection || dashboardSection) && <RateMultipleUpdatesButton />}
                        {addToReportButton}
                      </>
                    ) : (
                      <>
                        {addToReportButton}
                        <SaveUpdatesToFolder />
                        {(inboxSection || dashboardSection) && <RateMultipleUpdatesButton />}
                      </>
                    )}
                    {allInteractions && (
                      <button
                        className='contacts-list-bar-action-container-btn d-none d-lg-inline-flex'
                        onClick={() => setIsOpenAddTagsModal(true)}
                      >
                        <span className='icon-tag' style={{ fontSize: '18px', fontWeight: 600 }} />
                        Add a tag
                      </button>
                    )}
                    {allInteractions && (
                      <div
                        className='contacts-list-bar-action-main-container position-relative d-none d-lg-block'
                        ref={moreOptionsRef}
                      >
                        <button
                          className='contacts-list-bar-action-container-btn'
                          onClick={() => setIsOpenMoreOptions((prev) => !prev)}
                        >
                          <span
                            className={`more-option-button dropdown-item-element dropdown-item-element-13 ${
                              isOpenMoreOptions ? 'open' : ''
                            }`}
                          >
                            More
                          </span>
                        </button>
                        {isOpenMoreOptions && (
                          <div className='login-navbar py-2'>
                            <button
                              className='settings-button setting-item general-button d-block text-left w-100 position-relative text-danger-background'
                              onClick={() => {
                                setIsOpenMoreOptions(false);
                                setIsOpenDeleteModal(true);
                              }}
                            >
                              <span className='icon-delete mr-3' />
                              Delete {selectedUpdates.length === 1 ? 'interaction' : 'interactions'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {allInteractions && (
                    <div className='rounded-pill bg-main-white general-button specific-x-padding-12 specific-y-padding-3 flex-centered'>
                      <span className='icon-search light-50-text mr-2' />
                      <input
                        className='input-empty paragraph-p1'
                        placeholder='Search'
                        onKeyDown={(e) => {
                          if (e.keyCode === 13) {
                            const value = e.target.value?.trim();
                            const params = new URLSearchParams(location.search);
                            if (value === '') {
                              params.delete('search');
                            } else {
                              params.set('search', encodeURIComponent(value));
                            }
                            navigate({ search: `?${params.toString()}` });
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
                <button className='stakeholder-subnav-deselect-btn ml-2' onClick={resetSelectedUpdatesState}>
                  Cancel <span className='icon-close paragraph-p5 ml-2' />
                </button>
              </div>
            </div>
          </div>
          {allInteractions && (
            <RemoveInteractionsModal
              openState={{ isOpen: isOpenDeleteModal, setIsOpen: setIsOpenDeleteModal }}
              interactions={selectedUpdates}
              onSuccess={resetSelectedUpdatesState}
            />
          )}
          {allInteractions && (
            <AddTagsToInteractionsModal
              openState={{ isOpen: isOpenAddTagsModal, setIsOpen: setIsOpenAddTagsModal }}
              interactions={selectedUpdates}
              onSuccess={resetSelectedUpdatesState}
            />
          )}
        </div>
      )}
    </>
  );
};

const useMainActionsBarFunctions = () => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { temporarySelectedUpdates, selectedUpdates, selectedUpdatesIds } = state ?? {};
  const resetSelectedUpdatesState = () => {
    dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdates', value: [] });
    dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdatesIds', value: [] });
  };
  const restoreSelectedUpdates = () => {
    if (temporarySelectedUpdates) {
      const { selectedUpdates, selectedUpdatesIds } = temporarySelectedUpdates;
      dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdates', value: selectedUpdates });
      dispatch({ type: 'MODIFY_SECTION', parameter: 'selectedUpdatesIds', value: selectedUpdatesIds });
      dispatch({ type: 'MODIFY_SECTION', parameter: 'temporarySelectedUpdates', value: null });
    }
  };
  const setTemporarySelectedUpdates = () => {
    if (selectedUpdates?.length && selectedUpdatesIds?.length) {
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'temporarySelectedUpdates',
        value: { selectedUpdates, selectedUpdatesIds },
      });
    }
  };
  return { resetSelectedUpdatesState, restoreSelectedUpdates, setTemporarySelectedUpdates };
};

const SelectedCounter = ({ value }) => {
  const [display, setDisplay] = useState(value);
  const [anim, setAnim] = useState(null); // { prev, next, dir: 'inc'|'dec', key }

  useEffect(() => {
    if (value === display) return;
    const dir = value > display ? 'inc' : 'dec';
    setAnim({ prev: display, next: value, dir, key: Date.now() });
  }, [value, display]);

  const handleDone = () => {
    if (!anim) return;
    setDisplay(anim.next);
    setAnim(null);
  };

  return (
    <span className={`position-relative d-inline-block scounter ${value?.toString()?.length > 1 ? 'mr-1' : ''}`}>
      {/* idle state */}
      {!anim && <span className='scounter__static flex-centered'>{display}</span>}

      {/* animated swap */}
      {anim && (
        <>
          <span key={anim.key + '-out'} className={`flex-centered scounter__layer scounter__out scounter__${anim.dir}`}>
            {anim.prev}
          </span>
          <span
            key={anim.key + '-in'}
            className={`flex-centered scounter__layer scounter__in scounter__${anim.dir}`}
            onAnimationEnd={handleDone}
          >
            {anim.next}
          </span>
        </>
      )}
    </span>
  );
};

export { useMainActionsBarFunctions };
export default MainListActionsBar;
